import { motion } from "framer-motion";
import { Star, CreditCard } from "lucide-react";

const tiers = [
  {
    id: "left",
    name: "Left Foot Blaster",
    foot: "left",
    footLabel: "Left Foot Edition",
    blurb: "Tuned for left-foot lifters. Plant your left and let it fly.",
    featured: false,
  },
  {
    id: "both",
    name: "Both Feet — The Pair",
    foot: "both",
    footLabel: "Left + Right Set",
    blurb: "Buy one of each. Switch sides mid-mission or outfit a second throne.",
    badge: "Best Value",
    featured: true,
  },
  {
    id: "right",
    name: "Right Foot Blaster",
    foot: "right",
    footLabel: "Right Foot Edition",
    blurb: "Built for right-foot risers. Starboard-side superiority.",
    featured: false,
  },
];

const PriceSlot = ({ id }) => (
  <div
    data-testid={`price-slot-${id}`}
    className="border-2 border-dashed border-gold/50 rounded-md px-6 py-5 text-center bg-navy/60"
  >
    <span className="font-display font-black text-4xl text-cream/30">$ --.--</span>
    <p className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-gold mt-2">Add your price here</p>
  </div>
);

export const Pricing = ({ onPreorder }) => (
  <section id="pricing" data-testid="pricing-section" className="relative py-24 sm:py-32 grain-overlay">
    <div className="max-w-7xl mx-auto px-4 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-16"
      >
        <p className="font-jbmono text-xs uppercase tracking-[0.25em] text-crimson mb-4">Pricing & Pre-Order</p>
        <h2 className="font-display font-black uppercase text-3xl sm:text-5xl leading-none tracking-tight max-w-3xl">
          Lock yours in <span className="text-stroke-cream">before Christmas.</span>
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
        {tiers.map((t, i) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.12 }}
            data-testid={`pricing-tier-${t.id}`}
            className={`relative rounded-lg p-8 flex flex-col gap-6 ${
              t.featured
                ? "bg-gradient-to-b from-midnight to-navy border-2 border-crimson shadow-[0_20px_60px_rgba(230,57,70,0.25)] md:-mt-4 md:mb-[-1rem]"
                : "bg-midnight border border-steel/60"
            }`}
          >
            {t.badge && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-gold text-navy font-jbmono text-[10px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-sm whitespace-nowrap">
                <Star className="w-3 h-3 fill-navy" />
                {t.badge}
              </span>
            )}
            <div>
              <p className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-ice mb-2">{t.footLabel}</p>
              <h3 className="font-display font-black uppercase text-2xl tracking-tight">{t.name}</h3>
              <p className="text-sm text-cream/60 mt-2 leading-relaxed">{t.blurb}</p>
            </div>
            <PriceSlot id={t.id} />
            <div className="mt-auto flex flex-col gap-3">
              <button
                onClick={() => onPreorder(t.foot)}
                data-testid={`preorder-button-${t.id}`}
                className={`w-full font-display font-black uppercase tracking-wide text-lg px-6 py-4 rounded-sm transition-colors duration-200 ${
                  t.featured
                    ? "bg-crimson hover:bg-crimsondark text-white"
                    : "bg-steel/60 hover:bg-steel text-cream"
                }`}
              >
                Pre-Order Today
              </button>
              <button
                disabled
                data-testid={`paypal-placeholder-${t.id}`}
                className="w-full flex items-center justify-center gap-2 border border-dashed border-ice/50 text-ice/70 font-jbmono text-xs uppercase tracking-widest px-6 py-3 rounded-sm cursor-not-allowed"
              >
                <CreditCard className="w-4 h-4" />
                PayPal Checkout — link drops in here
              </button>
            </div>
          </motion.div>
        ))}
      </div>
      <p className="mt-10 text-center font-jbmono text-xs uppercase tracking-[0.2em] text-cream/40">
        Pre-orders are reservation requests — you only pay when the Master Blaster officially launches.
      </p>
    </div>
  </section>
);
